import { useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { useUser } from "@clerk/clerk-react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowRight, Briefcase, Building2, Megaphone } from "lucide-react";
import { raiseBoardApi } from "@/api/raise-board-api";
import { BoardAuthDialog } from "@/components/auth/board-auth-dialog";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { MultiSelect } from "@/components/ui/multi-select";
import { TooltipProvider } from "@/components/ui/tooltip";
import {
  RaiseBoardNav,
  RaiseBoardShell,
  RBCard,
  SectionLabel,
} from "./rb-ui";
import { BoardRow, HeroStat } from "./board-row";
import {
  getApplicantCompanyId,
  getFilterValues,
  getSource,
  matchesFilter,
  uniqueSorted,
  type Application,
} from "./deal-fields";
import { useMyVotes, useToggleUpvote } from "./use-raise-board-actions";

export default function RaiseBoardGlobal() {
  const navigate = useNavigate();
  const { isSignedIn } = useUser();

  const [industryFilter, setIndustryFilter] = useState<string[]>([]);
  const [businessModelFilter, setBusinessModelFilter] = useState<string[]>([]);
  const [sourceFilter, setSourceFilter] = useState<string[]>([]);
  const [authReason, setAuthReason] = useState<string | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ["raise-board-global"],
    queryFn: () => raiseBoardApi.getGlobal(),
    retry: false,
    // Keep the live board fresh — picks up other users' votes and re-ranks
    // when the tab regains focus.
    refetchOnWindowFocus: true,
  });

  const votedIds = useMyVotes(!!isSignedIn);
  const toggleVote = useToggleUpvote();

  const voteState = (application: Application) => ({
    voted: votedIds.has(String(application._id)),
    count: application.upvote_count ?? 0,
  });

  const handleVote = (application: Application) => {
    if (!isSignedIn) {
      setAuthReason("Sign in to upvote this deal.");
      return;
    }
    toggleVote.mutate(String(application._id));
  };

  const openDeal = (application: Application) => {
    const slug = getSource(application).slug;
    const applicantId = getApplicantCompanyId(application);
    if (!slug || !applicantId) {
      toast.error("This deal's profile isn't available.");
      return;
    }
    navigate(`/rb/${slug}/${applicantId}`, { state: { from: "/global" } });
  };

  // Memoized so the `?? []` fallback doesn't hand a fresh array to every
  // derived useMemo on each render.
  const applications = useMemo(
    () => data?.result?.applications ?? [],
    [data]
  );

  const filterOptions = useMemo(() => {
    const industries: string[] = [];
    const businessModels: string[] = [];
    const sources: string[] = [];
    for (const app of applications) {
      const v = getFilterValues(app);
      industries.push(...v.industries);
      businessModels.push(...v.businessModels);
      if (v.source) sources.push(v.source);
    }
    return {
      industries: uniqueSorted(industries),
      businessModels: uniqueSorted(businessModels),
      sources: uniqueSorted(sources),
    };
  }, [applications]);

  // Boards feeding the global list, with the deal count each contributes.
  // Derived from the same payload — no extra request.
  const boards = useMemo(() => {
    const byName = new Map<
      string,
      { name: string; slug?: string; enabled?: boolean; count: number }
    >();
    for (const app of applications) {
      const s = getSource(app);
      if (!s.name) continue;
      const existing = byName.get(s.name);
      if (existing) existing.count += 1;
      else
        byName.set(s.name, {
          name: s.name,
          slug: s.slug,
          enabled: s.enabled,
          count: 1,
        });
    }
    return Array.from(byName.values()).sort((a, b) => b.count - a.count);
  }, [applications]);

  const filtered = useMemo(() => {
    return applications.filter((app) => {
      const v = getFilterValues(app);
      return (
        matchesFilter(v.industries, industryFilter) &&
        matchesFilter(v.businessModels, businessModelFilter) &&
        matchesFilter(v.source ? [v.source] : [], sourceFilter)
      );
    });
  }, [applications, industryFilter, businessModelFilter, sourceFilter]);

  const hasFilters =
    industryFilter.length > 0 ||
    businessModelFilter.length > 0 ||
    sourceFilter.length > 0;

  const totalUpvotes = useMemo(
    () => applications.reduce((sum, a) => sum + (a.upvote_count ?? 0), 0),
    [applications]
  );

  const toOptions = (values: string[]) =>
    values.map((v) => ({ label: v, value: v }));

  if (error) {
    return (
      <RaiseBoardShell>
        <RaiseBoardNav onRequestAuth={setAuthReason} />
        <div className="flex flex-col items-center justify-center gap-3 px-4 py-32 text-center">
          <Megaphone className="h-10 w-10 text-muted-foreground/40" />
          <p className="font-serif text-2xl">
            The global Raiseboard isn't available
          </p>
          <p className="max-w-sm text-sm text-muted-foreground">
            Please try again in a moment.
          </p>
        </div>
      </RaiseBoardShell>
    );
  }

  return (
    <TooltipProvider delayDuration={200}>
      <RaiseBoardShell>
        <RaiseBoardNav onRequestAuth={setAuthReason} />

        {/* Editorial masthead: the pitch on the left, the board's scale on the
            right. Counts come from the payload already on the page. */}
        <div className="border-b border-border/40 bg-foreground text-background">
          <div className="mx-auto flex max-w-6xl flex-col gap-8 px-4 py-10 sm:px-6 lg:flex-row lg:items-center lg:justify-between lg:py-12">
            <div className="max-w-xl">
              <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-background/50">
                Powered by DiligenceGPT
              </p>
              <h2 className="mt-3 font-serif text-3xl leading-tight sm:text-4xl">
                The modern way to discover &amp; connect with startups actively
                raising capital.
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-background/70">
                A living deal room for investors, founders, and accelerators —
                profiles update in real time as startups raise and receive
                endorsements from trusted community members.
              </p>
            </div>

            <div className="flex flex-col items-start gap-5 lg:items-end lg:border-l lg:border-background/15 lg:pl-10">
              <div className="flex items-start gap-8">
                <HeroStat value={applications.length} label="Startups" />
                <HeroStat value={boards.length} label="Active boards" />
                <HeroStat value={totalUpvotes} label="Endorsements" />
              </div>
              {!isSignedIn && (
                <div className="lg:text-right">
                  <Button
                    onClick={() =>
                      setAuthReason("Create an account to join the Raiseboard.")
                    }
                    className="rounded-full bg-background px-5 text-foreground hover:bg-background/90"
                  >
                    Get started free
                    <ArrowRight className="h-4 w-4" />
                  </Button>
                  <p className="mt-2 text-[11px] text-background/50">
                    Free to join · No credit card required
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        <main className="mx-auto max-w-6xl px-4 py-8 sm:px-6 sm:py-10">
          <div className="flex flex-col gap-8 lg:flex-row lg:items-start">
            <div className="min-w-0 flex-1">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-red-600 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.12em] text-white">
                <span className="h-1.5 w-1.5 rounded-full bg-white" />
                Live
              </span>
              <h1 className="mt-4 font-serif text-5xl leading-none sm:text-6xl">
                Raiseboard
              </h1>
              <p className="mt-3 text-base text-muted-foreground">
                Startups actively raising right now — ranked by community
                momentum
              </p>
              <p className="mt-3 border-l-2 border-border pl-3 text-sm text-muted-foreground">
                {isLoading ? (
                  "Loading the board…"
                ) : (
                  <>
                    {filtered.length} compan
                    {filtered.length === 1 ? "y" : "ies"} raising
                    {hasFilters ? ` of ${applications.length}` : ""} · Updated
                    live
                  </>
                )}
              </p>

              {/* Filters: searchable multi-select dropdowns. Industry is
                  high-cardinality (GICS + legacy free-text), so a searchable
                  dropdown scales better than pills. */}
              <div className="mt-6 flex flex-wrap items-center gap-2">
                <MultiSelect
                  id="global-industry"
                  options={toOptions(filterOptions.industries)}
                  value={industryFilter}
                  onValueChange={setIndustryFilter}
                  placeholder="Industry"
                  triggerIcon={Building2}
                  maxCount={2}
                  className="w-auto min-w-[150px] rounded-full border-border bg-card"
                />
                <MultiSelect
                  id="global-business-model"
                  options={toOptions(filterOptions.businessModels)}
                  value={businessModelFilter}
                  onValueChange={setBusinessModelFilter}
                  placeholder="Business model"
                  triggerIcon={Briefcase}
                  maxCount={2}
                  className="w-auto min-w-[150px] rounded-full border-border bg-card"
                />
                <MultiSelect
                  id="global-source"
                  options={toOptions(filterOptions.sources)}
                  value={sourceFilter}
                  onValueChange={setSourceFilter}
                  placeholder="Raiseboard"
                  triggerIcon={Megaphone}
                  maxCount={2}
                  className="w-auto min-w-[150px] rounded-full border-border bg-card"
                />
              </div>

              <div className="mt-6 space-y-3">
                {isLoading ? (
                  [0, 1, 2].map((i) => (
                    <Skeleton key={i} className="h-[104px] w-full rounded-2xl" />
                  ))
                ) : filtered.length === 0 ? (
                  <RBCard className="py-12 text-center">
                    <Megaphone className="mx-auto h-8 w-8 text-muted-foreground/40" />
                    <p className="mt-3 text-sm text-muted-foreground">
                      No companies match the current filters.
                    </p>
                  </RBCard>
                ) : (
                  filtered.map((application, index) => (
                    <BoardRow
                      key={String(application._id)}
                      application={application}
                      rank={index + 1}
                      vote={voteState(application)}
                      onVote={() => handleVote(application)}
                      onOpen={() => openDeal(application)}
                    />
                  ))
                )}
              </div>
            </div>

            {/* Sidebar mirrors the reference's rail: where these deals come
                from, so a visitor can jump to a specific board. */}
            <aside className="w-full shrink-0 lg:sticky lg:top-24 lg:w-[300px]">
              <RBCard>
                <SectionLabel>Boards</SectionLabel>
                {isLoading ? (
                  <div className="mt-4 space-y-3">
                    <Skeleton className="h-10 w-full" />
                    <Skeleton className="h-10 w-full" />
                  </div>
                ) : boards.length === 0 ? (
                  <p className="mt-4 text-sm text-muted-foreground">
                    No boards are publishing to the global feed yet.
                  </p>
                ) : (
                  <ul className="mt-4 space-y-3">
                    {boards.slice(0, 6).map((board) => (
                      <li
                        key={board.name}
                        className="flex items-center justify-between gap-3"
                      >
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium">
                            {board.name}
                          </p>
                          <p className="text-[11px] uppercase tracking-[0.1em] text-muted-foreground">
                            {board.count} compan{board.count === 1 ? "y" : "ies"}
                          </p>
                        </div>
                        {board.slug && board.enabled && (
                          <a
                            href={`/rb/${board.slug}`}
                            target="_blank"
                            rel="noreferrer"
                            className="shrink-0 rounded-full border border-border px-3 py-1 text-xs font-medium transition-colors hover:bg-accent"
                          >
                            View
                          </a>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
                <div className="mt-5 border-t border-border pt-4">
                  {/* The rail shows the six biggest contributors; the directory
                      is where every published board lives. */}
                  <button
                    type="button"
                    onClick={() => navigate("/rb")}
                    className="inline-flex items-center gap-1.5 text-sm font-medium transition-colors hover:text-muted-foreground"
                  >
                    See all boards
                    <ArrowRight className="h-3.5 w-3.5" />
                  </button>
                </div>
                <div className="mt-4 border-t border-border pt-4">
                  <button
                    type="button"
                    onClick={() =>
                      isSignedIn
                        ? navigate("/workspaces")
                        : setAuthReason("Sign in to run your own Raiseboard.")
                    }
                    className="inline-flex items-center gap-1.5 text-sm font-medium transition-colors hover:text-muted-foreground"
                  >
                    Run your own board
                    <ArrowRight className="h-3.5 w-3.5" />
                  </button>
                </div>
              </RBCard>
            </aside>
          </div>
        </main>

        <BoardAuthDialog
          open={!!authReason}
          onOpenChange={(open) => {
            if (!open) setAuthReason(null);
          }}
          reason={authReason ?? undefined}
        />
      </RaiseBoardShell>
    </TooltipProvider>
  );
}
